import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Leaf } from 'lucide-react';

const navLinks = [
  { label: '关于小熊猫', href: '#about' },
  { label: '生活习性', href: '#behavior' },
  { label: '保护现状', href: '#conservation' },
  { label: '影像画廊', href: '#gallery' },
];

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background/95 border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('hero')}
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">
              <Leaf className="w-4 h-4 text-primary-foreground" />
            </div>
            <span
              className={`font-semibold text-lg tracking-tight transition-colors duration-300 ${
                scrolled ? 'text-foreground' : 'text-white'
              }`}
            >
              小熊猫之家
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href.slice(1))}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200 hover:bg-primary/10 hover:text-primary ${
                  scrolled ? 'text-foreground/80' : 'text-white/90 hover:bg-white/10 hover:text-white'
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('action')}
              className="ml-2 px-5 py-2 text-sm rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              参与保护
            </button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-foreground hover:bg-muted' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="菜单"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden bg-background/98 border-t border-border overflow-hidden"
            >
              <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => {
                      scrollToSection(link.href.slice(1));
                      setMenuOpen(false);
                    }}
                    className="text-left px-4 py-3 rounded-xl text-foreground/80 hover:bg-muted hover:text-primary transition-colors text-sm font-medium"
                  >
                    {link.label}
                  </button>
                ))}
                <button
                  onClick={() => {
                    scrollToSection('action');
                    setMenuOpen(false);
                  }}
                  className="mt-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-medium text-center hover:opacity-90 transition-opacity"
                >
                  参与保护
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-foreground text-background/70 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <Leaf className="w-4 h-4 text-primary-foreground" />
              </div>
              <div>
                <p className="font-semibold text-background">小熊猫之家</p>
                <p className="text-xs text-background/50">保护每一只红色精灵</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm">
                学名：
                <span className="font-mono text-primary/80 italic">Ailurus fulgens</span>
              </p>
              <p className="text-xs mt-1 text-background/40">
                图片来源：Unsplash · 内容参考：IUCN · Wikipedia · 台北市立动物园
              </p>
            </div>
            <div className="text-xs text-background/40 text-right">
              <p>© 2026 小熊猫之家</p>
              <p className="mt-1">保护濒危动物，从了解开始</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
